import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useParams, matchPath, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { useToggle } from 'react-use';
import Badge from '@mui/material/Badge';
import styled from 'styled-components';
import { IconButton } from '../styled';
import RefundIcon from '../../../assets/icons/Refund';
import Title from '../Header/Title';
import { ButtonRounded } from '../../../components/atoms';
import { useCommentsContext } from '../CommentsDrawerContent/CommentsContext';
import { UI_ROUTES } from '../../../constants/routes';
import RefundDialog from './RefundDialog';
import { isDefined } from '../../../utils/helpers';
import { useGetViews } from '../../../hooks/useGetView';
import usePermission from '../../../permissions/hooks/usePermission';
import { PAYMENTS_PERMISSIONS_IDS } from '../permissions';
import OptionsMenu from '../../../components/atoms/OptionsMenu';
import { usePaymentsDetailsContext } from '../../DataTables/PaymentDetails/PaymentDetailsContext';
import CommentIcon from '../../../assets/icons/Comment';
import { useFeature } from '../../../hooks/useFeature';
import { FEATURE_TOGGLES_KEYS } from '../../../constants/featureToggles';
import CancelDialog from './CancelDialog';
import { useGetPaymentDetailsData } from './hooks/useGetPaymentDetailsData';

const PAYMENT_ISSUES_ROUTE_MATCH = '/payments/payment-issues/details/:issuesId/payments/:detailsId';

const StyledBadge = styled(({ active, ...props }) => <Badge {...props} />)`
  color: ${({ active }) => (active ? 'white' : 'rgb(120, 127, 136)')};
`;

const Header = ({ toggleDrawer, isIntent }) => {
  const { t } = useTranslation();
  const { toggleCreateIssuesModal } = usePaymentsDetailsContext();
  const { payment } = useGetPaymentDetailsData();

  const paymentStatus = payment?.status;

  const location = useLocation();
  const { detailsId, issueId, viewsId } = useParams();
  const [hasPaymentManagementPermission] = usePermission(PAYMENTS_PERMISSIONS_IDS.paymentsManagement);
  const [paymentIssuesNavEnabled] = useFeature(FEATURE_TOGGLES_KEYS.PAYMENT_ISSUES_SUB_NAV);

  const matchIssuesPage = matchPath(location?.pathname, PAYMENT_ISSUES_ROUTE_MATCH);
  const isIssuesDetails = issueId && isDefined(matchIssuesPage);

  const savedViews = useGetViews();
  const currentSavedViewName = savedViews?.[viewsId]?.name;

  const primary = useMemo(() => {
    const searchParams = location?.search || '';
    switch (true) {
      case isIssuesDetails:
        return {
          title: 'Issues',
          route: `${UI_ROUTES.payments}/payment-issues${searchParams}`,
        };
      case !!viewsId:
        return {
          title: currentSavedViewName || 'Saved View',
          route: `${UI_ROUTES.payments}/views/${viewsId}${searchParams}`,
        };
      default:
        return {
          title: 'All',
          route: UI_ROUTES.payments,
        };
    }
  }, [isIssuesDetails, currentSavedViewName, viewsId, location?.search]);

  const { newCommentsCount } = useCommentsContext();
  const [modalRefundShown, toggleShowModalRefund] = useToggle(false);
  const [modalCancelShown, toggleShowModalCancel] = useToggle(false);

  const showButtonRefund = hasPaymentManagementPermission && paymentStatus === 'SUCCEEDED';
  const showButtonCancel = hasPaymentManagementPermission && paymentStatus === 'AUTHORISED';

  return (
    <Box display="flex" minHeight="40px">
      <Box display="flex" alignItems="center" flexGrow="1" width="auto" overflow="hidden">
        <Title primary={primary} secondary={detailsId || issueId} />
      </Box>

      <Box display="flex" alignItems="center">
        {!isIntent && showButtonRefund && (
          <Box>
            <ButtonRounded
              onClick={toggleShowModalRefund}
              type="button"
              variant="contained"
              color="secondary"
              startIcon={<RefundIcon />}
            >
              <Box component="span" color="#000">
                {t('payments.refund')}
              </Box>
            </ButtonRounded>
          </Box>
        )}

        {!isIntent && showButtonCancel && (
          <Box>
            <ButtonRounded
              onClick={toggleShowModalCancel}
              type="button"
              variant="contained"
              color="secondary"
              startIcon={<RefundIcon />}
            >
              <Box component="span" color="#000">
                {t('payments.cancel')}
              </Box>
            </ButtonRounded>
          </Box>
        )}

        {detailsId && hasPaymentManagementPermission && (
          <>
            <Box pl={2}>
              <IconButton
                bgcolor={newCommentsCount > 0 ? '#3023C8' : '#F5F6F7'}
                type="button"
                onClick={toggleDrawer}
                height="40px"
                width="40px"
                size="large"
              >
                <StyledBadge color="secondary" active={newCommentsCount > 0} badgeContent={newCommentsCount}>
                  <CommentIcon color="inherit" />
                </StyledBadge>
              </IconButton>
            </Box>

            {!isIntent && paymentIssuesNavEnabled && (
              <Box pl={2}>
                <OptionsMenu
                  color="#000"
                  options={[
                    {
                      label: 'Create issue',
                      onClick: toggleCreateIssuesModal,
                      disabled: issueId,
                    },
                  ]}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <RefundDialog isOpen={modalRefundShown} toggleIsOpen={toggleShowModalRefund} />
      <CancelDialog isOpen={modalCancelShown} toggleIsOpen={toggleShowModalCancel} amount={payment?.amount?.value} />
    </Box>
  );
};

Header.propTypes = {
  toggleDrawer: PropTypes.func.isRequired,
  isIntent: PropTypes.bool.isRequired,
};

export default Header;
